import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked, viewChild, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { BadgeModule } from 'primeng/badge';
import { SkeletonModule } from 'primeng/skeleton';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TieredMenuModule, TieredMenu } from 'primeng/tieredmenu';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { SlidePanelComponent } from '../slide-panel/slide-panel.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { LoadFailedComponent } from '../page-states/load-failed.component';
import { LocaleChipComponent } from '../locale-chip/locale-chip.component';
import { formatCount } from '../../utils/count.utils';
import { UploadQueueComponent } from '../upload-queue/upload-queue.component';
import { FeedbackPanelComponent } from '../feedback-panel/feedback-panel.component';
import { TemporalPanelComponent } from '../temporal-panel/temporal-panel.component';
import { ActivitySubjectOpenService } from '../activity-events/activity-subject-open.service';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import {
  faCloudArrowUp,
  faBell,
  faGlobe,
  faGear,
  faChevronDown,
  faPlus,
  faShieldHalved,
  faRightFromBracket,
  faCommentDots,
  faUser,
  faCircleUser,
  faKeyboard,
  faLifeRing,
  faMagnifyingGlass,
  faClockRotateLeft,
  faPaperPlane,
  faXmark,
  faBookmark,
  faEyeSlash,
  faShieldAlt,
  faArrowsRotate,
} from '@fortawesome/pro-duotone-svg-icons';
import { CommandPaletteService } from '../command-palette/command-palette.service';
import { KeyboardShortcutRegistryService } from '../../services/keyboard-shortcut-registry.service';

import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { LocaleService } from '../../services/locale.service';
import { UploadService } from '../../services/upload.service';
import { SlidePanelService } from '../../services/slide-panel.service';
import { PreferencesService } from '../../services/preferences.service';
import { MembershipService } from '../../services/membership.service';
import { SystemAccessService } from '../../services/system-access.service';
import { ConnectivityService } from '../../services/connectivity.service';
import { NotificationService, RenderedNotification } from '../../services/notification.service';
import { FilePreviewService } from '../../services/file-preview.service';
import { AnalyticsCollectorService } from '../../services/analytics-collector.service';
import { ShareArrivalService } from '../../services/share-arrival.service';
import { ItemService } from '../../services/item.service';
import { buildItemUrl, buildItemUrlFromParts } from '../../utils/url.utils';
import { OPEN_SEARCH_SHORTCUT } from '../shortcut-help/shortcut-manifest';
import { ItemType } from '../../models/item';
import {
  commentLandingFor,
  NotificationRouteDescriptor,
  resolveArrivalCorrelation,
  resolveMetadataDestinationRoute,
  resolveNotificationRoute,
} from './topbar-routing';
import { BookmarkService } from '../../services/bookmark.service';
import { type BookmarkItem } from '../../graphql/bookmark-operations';
import { AppConfigService } from '../../services/app-config.service';
import { DemoModeService } from '../../services/demo-mode.service';
import { TopbarDisplay } from '../../models/app-config.model';
import { isAdminOrHigher } from '../../models/member.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TourTargetDirective } from '../../directives/tour-target.directive';
import { TourService } from '../../services/tour.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { TemporalPanelService } from '../../services/temporal-panel.service';
import { environment } from '../../../environments/environment';
import { buildAccountAppUrl } from '../../utils/account-url.util';
import { raiseFailureToast } from '../../utils/failure-toast.utils';
import { primaryCodeOf } from '@branduals/graphql-errors';
import { UnsavedWorkPromptService, raiseConfirm, registerUnsavedWork } from '@branduals/dialog-guard';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    BadgeModule,
    SkeletonModule,
    UserAvatarComponent,
    DividerModule,
    TooltipModule,
    FontAwesomeModule,
    TieredMenuModule,
    SlidePanelComponent,
    UploadQueueComponent,
    FeedbackPanelComponent,
    TemporalPanelComponent,
    StatusBadgeComponent,
    LoadFailedComponent,
    LocaleChipComponent,
    TranslateModule,
    TourTargetDirective
  ],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarComponent {
  protected readonly formatCount = formatCount;

  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private localeService = inject(LocaleService);
  protected readonly locale = this.localeService.activeContentLanguage;
  private uploadService = inject(UploadService);
  private slidePanelService = inject(SlidePanelService);
  private preferencesService = inject(PreferencesService);
  private membershipService = inject(MembershipService);
  protected readonly access = inject(SystemAccessService);
  // The reconnecting chip's one read: the live-updates-paused state (the template's chip comment).
  protected readonly connectivity = inject(ConnectivityService);
  notificationService = inject(NotificationService);
  private itemService = inject(ItemService);
  private filePreviewService = inject(FilePreviewService);
  /** The journal's Open door owns the ONE "isn't available" voice; the bell's gone-subject landing speaks through it. */
  private readonly activitySubjectOpen = inject(ActivitySubjectOpenService);
  protected readonly bookmarkService = inject(BookmarkService);
  private appConfigService = inject(AppConfigService);
  private demoModeService = inject(DemoModeService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private readonly analytics = inject(AnalyticsCollectorService);
  private readonly shareArrival = inject(ShareArrivalService);
  private commandPaletteService = inject(CommandPaletteService);
  private readonly shortcutRegistry = inject(KeyboardShortcutRegistryService);
  private tour = inject(TourService);
  protected readonly temporal = inject(TemporalContextService);
  private readonly leavePrompt = inject(UnsavedWorkPromptService);
  private readonly temporalPanelService = inject(TemporalPanelService);

  private destroyRef = inject(DestroyRef);
  private panelAutoCloseTimer: ReturnType<typeof setTimeout> | null = null;
  /** The drawer the active tour step asked us to reveal, so we can close exactly it on leave. */
  private revealedPanel: SlidePanelComponent | null = null;

  constructor() {
    // Sync UI dropdowns with preference signal changes
    effect(() => {
      const prefs = this.preferencesService.preferences();
      if (prefs) {
        if (prefs.displayLanguage) {
          this.selectedUILanguage.set(prefs.displayLanguage);
        }
        // Content language: seed the dropdown from the RESOLVED active language (the person's pick for
        // this brand, else the documented order). Reading the active signal also re-syncs the dropdown
        // whenever resolution updates the active language.
        this.selectedContentLanguage.set(this.localeService.getActiveContentLanguage());
        if (prefs.timeZone) {
          const timezoneExists = this.timezones.some(tz => tz.value === prefs.timeZone);
          if (timezoneExists) {
            this.selectedTimezone.set(prefs.timeZone);
          }
        }
        if (prefs.theme) {
          this.selectedTheme = prefs.theme;
        }
      }
    });

    // The feedback draft lives in app chrome and survives every route change, so a route leave
    // destroys nothing — only a tab close/reload loses it. Gated on the panel being OPEN: a
    // programmatic force-close (a tour step leaving) keeps the hidden text alive for the next
    // open rather than losing it, and hidden text must not arm the native prompt.
    registerUnsavedWork({
      isDirty: () => (this.feedbackPanel()?.isOpen() ?? false) && this.feedbackDirty(),
      scope: 'unload-only',
    });

    // A Discard answered at the EVICTION prompt must mean what it said. The mutex evicts the
    // feedback panel by force-closing it, and the projected content survives hidden — so the
    // "discarded" text would otherwise be waiting on the next open. The slot moving from the
    // feedback panel to ANOTHER panel while the panel was GUARDED is exactly an approved
    // eviction (a refused one never moves the slot; a system open yields). An UNGUARDED eviction
    // — the panel mid-send, where in-flight work counts as committed and nothing is asked — keeps
    // the text: no Discard was answered, and a send that then fails still has its text for a
    // retry (ruled 2026-09-01). A slot emptied to null is a force-close or the panel's own
    // post-send close, which keep the hidden-text posture recorded above.
    effect(() => {
      const current = this.slidePanelService.currentPanel();
      const previous = this.lastPanelSlot;
      this.lastPanelSlot = current;
      const feedback = untracked(() => this.feedbackPanel());
      if (!feedback || previous !== feedback || !current || current === feedback) return;
      if (!untracked(() => feedback.dismissGuarded())) return;
      untracked(() => this.feedbackContent()?.discardDraft());
    });

    // Hollow-state pages request the Time-travel panel ("Pick another date") through
    // TemporalPanelService — trigger read first, body untracked (playbook §4). A member gesture
    // delivered through a request counter: it may evict a dirty panel after asking, and the
    // density strip loads only when the panel actually opened.
    effect(() => {
      const requests = this.temporalPanelService.openRequests();
      if (requests === 0) return; // init fire — no request yet
      untracked(() => {
        const panel = this.timeTravelPanel();
        if (panel && !panel.isOpen()) {
          void this.slidePanelService.openPanel(panel, { intent: 'gesture' }).then(opened => {
            if (opened) void this.timeTravelContent()?.load();
          });
        }
      });
    });

    // Auto-open upload panel when uploads start — a SYSTEM open: it yields silently rather than
    // ambush a member typing in a guarded panel (uploads continue; the topbar chip shows them).
    this.uploadService.panelRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearPanelAutoClose();
        // Route the auto-open through the service so it joins single-panel arbitration
        // (closes any other open panel + becomes current) instead of overlaying it — the
        // user-facing uploads button already toggles via the service (template + topbar).
        const panel = this.uploadsPanel();
        if (panel) void this.slidePanelService.openPanel(panel, { intent: 'system' });
      });

    // Auto-close upload panel 30s after all uploads complete
    this.uploadService.allUploadsCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearPanelAutoClose();
        this.panelAutoCloseTimer = setTimeout(() => {
          this.uploadsPanel()?.close();
          this.panelAutoCloseTimer = null;
        }, 30000);
      });

    // Tour reveal bridge: a step may ask to open one of the topbar drawers so the tour can
    // show its contents (not just glow the icon). Open on enter, close on leave / tour end.
    // openPanel() joins single-panel arbitration, so moving between two reveal steps closes
    // the previous drawer automatically.
    effect(() => {
      const panel = this.revealPanelFor(this.tourRevealKey());
      if (panel) {
        // A SYSTEM open: a tour step must never ambush a member typing in a guarded drawer, so
        // it yields — and the reveal is recorded only when the drawer actually opened.
        void this.slidePanelService.openPanel(panel, { intent: 'system' }).then(opened => {
          if (opened) this.revealedPanel = panel;
        });
      } else if (this.revealedPanel) {
        this.revealedPanel.close();
        this.revealedPanel = null;
      }
    });
  }

  /** Reveal key of the active tour step (e.g. 'globalization'), or null when none/no tour. */
  protected readonly tourRevealKey = computed(() => this.tour.currentStep()?.reveal ?? null);

  private revealPanelFor(key: string | null): SlidePanelComponent | undefined {
    switch (key) {
      case 'globalization': return this.globalizationPanel();
      case 'feedback':      return this.feedbackPanel();
      case 'account':       return this.profilePanel();
      default:              return undefined;
    }
  }

  private clearPanelAutoClose(): void {
    if (this.panelAutoCloseTimer) {
      clearTimeout(this.panelAutoCloseTimer);
      this.panelAutoCloseTimer = null;
    }
  }

  // ViewChild references for panels
  readonly uploadsPanel = viewChild<SlidePanelComponent>('uploadsPanel');
  readonly notificationsPanel = viewChild<SlidePanelComponent>('notificationsPanel');
  readonly bookmarksPanel = viewChild<SlidePanelComponent>('bookmarksPanel');
  readonly globalizationPanel = viewChild<SlidePanelComponent>('globalizationPanel');
  readonly settingsPanel = viewChild<SlidePanelComponent>('settingsPanel');
  readonly feedbackPanel = viewChild<SlidePanelComponent>('feedbackPanel');
  /** The mutex slot as this component last saw it — the eviction-clear effect reads the transition. */
  private lastPanelSlot: SlidePanelComponent | null = null;
  // Queried by TYPE, not by template ref: a host rendering a stand-in for the feedback content
  // then reads "no such component" (undefined) instead of an element that lacks the API.
  readonly feedbackContent = viewChild(FeedbackPanelComponent);
  /** The feedback draft's dirty fact, bridged from the content component that owns the text. */
  protected readonly feedbackDirty = computed(() => this.feedbackContent()?.dirty() ?? false);
  readonly profilePanel = viewChild<SlidePanelComponent>('profilePanel');
  readonly timeTravelPanel = viewChild<SlidePanelComponent>('timeTravelPanel');
  readonly timeTravelContent = viewChild<TemporalPanelComponent>('timeTravelContent');
  // Search input removed - now using CommandPaletteComponent
  readonly brandMenu = viewChild<TieredMenu>('brandMenu');

  // Font Awesome icons
  faCloudArrowUp = faCloudArrowUp;
  faBell = faBell;
  faGlobe = faGlobe;
  faGear = faGear;
  faChevronDown = faChevronDown;
  faPlus = faPlus;
  faShieldHalved = faShieldHalved;
  faRightFromBracket = faRightFromBracket;
  faCommentDots = faCommentDots;
  faUser = faUser;
  faCircleUser = faCircleUser;
  faKeyboard = faKeyboard;
  faLifeRing = faLifeRing;
  // Help & support destination — the help hub on the marketing site (per-environment address).
  readonly helpUrl = `${environment.marketingBaseUrl}/help`;
  faMagnifyingGlass = faMagnifyingGlass;
  faClockRotateLeft = faClockRotateLeft;
  faPaperPlane = faPaperPlane;
  faXmark = faXmark;
  faBookmark = faBookmark;
  faEyeSlash = faEyeSlash;
  faShieldAlt = faShieldAlt;
  faArrowsRotate = faArrowsRotate;

  // Bookmark state
  protected readonly bookmarks = this.bookmarkService.bookmarks;
  protected readonly bookmarksLoading = this.bookmarkService.loading;
  protected readonly bookmarksFailed = this.bookmarkService.loadFailed;

  // Signals
  isAuthenticated = computed(() => this.authService.authenticated() || this.demoModeService.isDemo());
  currentUser = computed<any>(() => {
    if (this.demoModeService.isDemo()) {
      return { name: 'Demo', email: 'demo@branduals.com' };
    }
    return this.authService.user();
  });
  userPicture = computed(() => this.membershipService.currentUser()?.picture ?? this.authService.user()?.picture);
  /** Stable identity key for the avatar color — platform user id, same id space as member.id. */
  userColorKey = computed(() => this.membershipService.currentUser()?.id ?? this.authService.user()?.sub);
  currentBrand = signal<any>(null);
  isAdmin = computed(() => {
    if (this.demoModeService.isDemo()) return true;
    const brand = this.currentBrand();
    return brand ? isAdminOrHigher(brand.privilegeLevel) : false;
  });
  brands = signal<any[]>([]);
  brandMenuItems: MenuItem[] = [];

  // Logo signals — reactive to AppConfig changes (instant when settings page updates)
  readonly topbarDisplayMode = computed(() => this.appConfigService.config$()?.topbarDisplay ?? TopbarDisplay.Name);
  readonly showLogo = computed(() => this.topbarDisplayMode() === TopbarDisplay.Logo || this.topbarDisplayMode() === TopbarDisplay.LogoAndName);
  readonly showBrandName = computed(() => this.topbarDisplayMode() === TopbarDisplay.Name || this.topbarDisplayMode() === TopbarDisplay.LogoAndName);
  readonly logoUrl = computed(() => {
    const config = this.appConfigService.config$();
    if (!config) return null;
    // logoLight = uploaded on white card = for light backgrounds
    // logoDark = uploaded on dark card = for dark backgrounds
    const isDark = this.themeService.effectiveTheme() === 'dark';
    return (isDark ? config.logoDark : config.logoLight)
        ?? (isDark ? config.logoLight : config.logoDark);
  });
  readonly brandDisplayName = computed(() => this.appConfigService.config$()?.brandName ?? '');

  /** Whether the sign-in door may offer sign-up — the membership service's one predicate over the anonymous tenant config. */
  readonly signupAllowed = this.membershipService.signupAllowed;

  // Membership state (computed from MembershipService)
  membershipState = this.membershipService.membershipState;
  canJoinDirectly = this.membershipService.canJoinDirectly;
  hasAwaitingInvitation = this.membershipService.hasAwaitingInvitation;
  canRequestToJoin = this.membershipService.canRequestToJoin;
  isBrandClosed = this.membershipService.isBrandClosed;
  /** The pending pill's Withdraw door is in flight — the button shows its spinner and refuses a second press. */
  withdrawing = signal(false);
  /** The reconcile re-read behind a refused withdraw failed — the kept pending pill says it may be stale. */
  reconcileFailed = this.membershipService.reconcileFailed;

  /** The stale status's Try again — the same re-read the reconcile ran. */
  retryReconcile(): void {
    this.membershipService.retryReconcile();
  }

  // The member chrome's ONE admission signal — "authenticated, loaded, confirmed member" — for the
  // account strip, feedback, uploads and notifications. Read from one signal in the template so
  // the @if re-evaluates on every relevant change.
  readonly isMember = this.membershipService.isAdmitted;

  // The search trigger opens for a member AND for a public brand's visitor (the public content is
  // searchable), so it reads admission OR public mode — the same pair the command palette's own
  // guard reads, so the visible trigger and the live hotkeys never disagree. The admission half stays
  // the bare membership predicate on purpose: a monitoring root has no trigger today and gains none.
  readonly searchAvailable = computed(() => this.membershipService.isAdmitted() || this.membershipService.publicMode());

  // Search is now handled by CommandPaletteService

  // Notifications
  notificationCount = this.notificationService.unreadCount;
  notificationItems = this.notificationService.notifications;
  notificationsLoading = this.notificationService.isLoading;
  notificationsHasMore = this.notificationService.hasNextPage;
  notificationsFailed = this.notificationService.loadFailed;

  /** Page past the first 20 (the service's cursor machinery was never called). */
  loadMoreNotifications(): void {
    void this.notificationService.fetchNotifications(false);
  }

  /** The failure arm's Try again — a fresh first page, never a cursor continuation. */
  retryNotifications(): void {
    void this.notificationService.fetchNotifications(true);
  }
  
  // Uploads
  activeUploadsCount = this.uploadService.activeUploadsCount;
  alertUploadsCount = this.uploadService.alertsCount;
  protected readonly hasActiveUploads = computed(() =>
    this.uploadService.batches().length > 0
  );
  
  // Theme
  currentTheme = this.themeService.currentTheme;
  selectedTheme: 'light' | 'dark' | 'auto' = 'auto';
  
  // Interface languages — the platform whitelist (user-level, never brand-scoped). Labels are the
  // curated Title-case endonyms (the namer's endonym arm), not raw Intl.DisplayNames output: a member
  // who cannot read the interface language must still find their own by its self-name.
  uiLanguages = this.localeService.getSupportedUILanguages().map(code => ({
    label: this.localeService.languageName(code, { endonym: true }),
    value: code
  }));
  selectedUILanguage = signal('en-US');

  // Content Languages — the brand's offer from config (reactive to settings changes), READ IN THE
  // READER'S LANGUAGE through the one namer ("Czech", "English" for an English reader — an offer is
  // read in the reader's language, never in a self-name computed from the code).
  contentLanguages = computed(() => {
    const config = this.appConfigService.config$();
    const codes = (config?.locales ?? []).map(l => l.id);
    return codes.map(code => ({
      label: this.localeService.languageName(code, { among: codes }),
      value: code
    }));
  });
  selectedContentLanguage = signal('en-US');

  protected isLocaleHidden(code: string): boolean {
    return this.localeService.isContentLanguageHidden(code);
  }

  /** Template-visible wrapper (localeService is private — strict template check). */
  protected readonly activeContentLanguageHidden = this.localeService.activeContentLanguageHidden;
  /** The interface language outside the brand's content offer — the panel's sentence and the globe button's state line disclose it. */
  protected readonly contentOfferMismatch = this.localeService.contentOfferMismatch;
  /**
   * The two languages the disclosure names, through the one namer in the interface language and AMONG
   * the brand's offer (ruled 2026-09-12): where the offer holds a sibling variant of the content language
   * the name carries its region ("English (United Kingdom)"), so a reader learns which one they read; the
   * interface language has no sibling in the offer while the disclosure shows (the fact compares by
   * language), so its name stays plain by construction.
   */
  protected readonly uiLanguageName = computed(() =>
    this.localeService.languageName(this.localeService.activeUILanguage(), { among: this.localeService.getSupportedContentLanguages() }));
  protected readonly contentLanguageName = computed(() =>
    this.localeService.languageName(this.localeService.activeContentLanguage(), { among: this.localeService.getSupportedContentLanguages() }));
  
  // Timezones - formatted like Windows timezone selector
  timezones = [
    // UTC-12:00 to UTC-11:00
    { label: '(UTC-12:00) International Date Line West', value: 'Etc/GMT+12' },
    { label: '(UTC-11:00) Coordinated Universal Time-11', value: 'Pacific/Midway' },
    { label: '(UTC-10:00) Hawaii', value: 'Pacific/Honolulu' },
    { label: '(UTC-09:30) Marquesas Islands', value: 'Pacific/Marquesas' },
    { label: '(UTC-09:00) Alaska', value: 'America/Anchorage' },
    { label: '(UTC-08:00) Baja California', value: 'America/Tijuana' },
    { label: '(UTC-08:00) Pacific Time (US & Canada)', value: 'America/Los_Angeles' },
    { label: '(UTC-07:00) Arizona', value: 'America/Phoenix' },
    { label: '(UTC-07:00) Chihuahua, La Paz, Mazatlan', value: 'America/Chihuahua' },
    { label: '(UTC-07:00) Mountain Time (US & Canada)', value: 'America/Denver' },
    { label: '(UTC-06:00) Central America', value: 'America/Guatemala' },
    { label: '(UTC-06:00) Central Time (US & Canada)', value: 'America/Chicago' },
    { label: '(UTC-06:00) Guadalajara, Mexico City, Monterrey', value: 'America/Mexico_City' },
    { label: '(UTC-06:00) Saskatchewan', value: 'America/Regina' },
    { label: '(UTC-05:00) Bogota, Lima, Quito', value: 'America/Bogota' },
    { label: '(UTC-05:00) Eastern Time (US & Canada)', value: 'America/New_York' },
    { label: '(UTC-05:00) Indiana (East)', value: 'America/Indiana/Indianapolis' },
    { label: '(UTC-04:00) Atlantic Time (Canada)', value: 'America/Halifax' },
    { label: '(UTC-04:00) Caracas', value: 'America/Caracas' },
    { label: '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan', value: 'America/La_Paz' },
    { label: '(UTC-04:00) Santiago', value: 'America/Santiago' },
    { label: '(UTC-03:30) Newfoundland', value: 'America/St_Johns' },
    { label: '(UTC-03:00) Brasilia', value: 'America/Sao_Paulo' },
    { label: '(UTC-03:00) Buenos Aires', value: 'America/Argentina/Buenos_Aires' },
    { label: '(UTC-03:00) Greenland', value: 'America/Godthab' },
    { label: '(UTC-03:00) Montevideo', value: 'America/Montevideo' },
    { label: '(UTC-02:00) Mid-Atlantic', value: 'Atlantic/South_Georgia' },
    { label: '(UTC-01:00) Azores', value: 'Atlantic/Azores' },
    { label: '(UTC-01:00) Cape Verde Is.', value: 'Atlantic/Cape_Verde' },
    
    // UTC/GMT
    { label: '(UTC) Coordinated Universal Time', value: 'UTC' },
    { label: '(UTC+00:00) Dublin, Edinburgh, Lisbon, London', value: 'Europe/London' },
    { label: '(UTC+00:00) Monrovia, Reykjavik', value: 'Africa/Monrovia' },
    { label: '(UTC+00:00) Casablanca', value: 'Africa/Casablanca' },
    
    // Europe & Africa
    { label: '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna', value: 'Europe/Berlin' },
    { label: '(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague', value: 'Europe/Prague' },
    { label: '(UTC+01:00) Brussels, Copenhagen, Madrid, Paris', value: 'Europe/Paris' },
    { label: '(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb', value: 'Europe/Warsaw' },
    { label: '(UTC+01:00) West Central Africa', value: 'Africa/Lagos' },
    { label: '(UTC+02:00) Athens, Bucharest', value: 'Europe/Athens' },
    { label: '(UTC+02:00) Beirut', value: 'Asia/Beirut' },
    { label: '(UTC+02:00) Cairo', value: 'Africa/Cairo' },
    { label: '(UTC+02:00) Damascus', value: 'Asia/Damascus' },
    { label: '(UTC+02:00) Harare, Pretoria', value: 'Africa/Johannesburg' },
    { label: '(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius', value: 'Europe/Helsinki' },
    { label: '(UTC+02:00) Jerusalem', value: 'Asia/Jerusalem' },
    { label: '(UTC+02:00) Kaliningrad', value: 'Europe/Kaliningrad' },
    { label: '(UTC+03:00) Baghdad', value: 'Asia/Baghdad' },
    { label: '(UTC+03:00) Istanbul', value: 'Europe/Istanbul' },
    { label: '(UTC+03:00) Kuwait, Riyadh', value: 'Asia/Riyadh' },
    { label: '(UTC+03:00) Minsk', value: 'Europe/Minsk' },
    { label: '(UTC+03:00) Moscow, St. Petersburg', value: 'Europe/Moscow' },
    { label: '(UTC+03:00) Nairobi', value: 'Africa/Nairobi' },
    { label: '(UTC+03:30) Tehran', value: 'Asia/Tehran' },
    
    // Asia
    { label: '(UTC+04:00) Abu Dhabi, Muscat', value: 'Asia/Dubai' },
    { label: '(UTC+04:00) Baku', value: 'Asia/Baku' },
    { label: '(UTC+04:00) Tbilisi', value: 'Asia/Tbilisi' },
    { label: '(UTC+04:00) Yerevan', value: 'Asia/Yerevan' },
    { label: '(UTC+04:30) Kabul', value: 'Asia/Kabul' },
    { label: '(UTC+05:00) Ashgabat, Tashkent', value: 'Asia/Tashkent' },
    { label: '(UTC+05:00) Islamabad, Karachi', value: 'Asia/Karachi' },
    { label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi', value: 'Asia/Kolkata' },
    { label: '(UTC+05:30) Sri Jayawardenepura', value: 'Asia/Colombo' },
    { label: '(UTC+05:45) Kathmandu', value: 'Asia/Kathmandu' },
    { label: '(UTC+06:00) Almaty, Novosibirsk', value: 'Asia/Almaty' },
    { label: '(UTC+06:00) Astana', value: 'Asia/Dhaka' },
    { label: '(UTC+06:30) Yangon (Rangoon)', value: 'Asia/Yangon' },
    { label: '(UTC+07:00) Bangkok, Hanoi, Jakarta', value: 'Asia/Bangkok' },
    { label: '(UTC+07:00) Krasnoyarsk', value: 'Asia/Krasnoyarsk' },
    { label: '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi', value: 'Asia/Shanghai' },
    { label: '(UTC+08:00) Irkutsk', value: 'Asia/Irkutsk' },
    { label: '(UTC+08:00) Kuala Lumpur, Singapore', value: 'Asia/Singapore' },
    { label: '(UTC+08:00) Perth', value: 'Australia/Perth' },
    { label: '(UTC+08:00) Taipei', value: 'Asia/Taipei' },
    { label: '(UTC+08:00) Ulaanbaatar', value: 'Asia/Ulaanbaatar' },
    { label: '(UTC+08:30) Pyongyang', value: 'Asia/Pyongyang' },
    { label: '(UTC+09:00) Osaka, Sapporo, Tokyo', value: 'Asia/Tokyo' },
    { label: '(UTC+09:00) Seoul', value: 'Asia/Seoul' },
    { label: '(UTC+09:00) Yakutsk', value: 'Asia/Yakutsk' },
    { label: '(UTC+09:30) Adelaide', value: 'Australia/Adelaide' },
    { label: '(UTC+09:30) Darwin', value: 'Australia/Darwin' },
    { label: '(UTC+10:00) Brisbane', value: 'Australia/Brisbane' },
    { label: '(UTC+10:00) Canberra, Melbourne, Sydney', value: 'Australia/Sydney' },
    { label: '(UTC+10:00) Guam, Port Moresby', value: 'Pacific/Port_Moresby' },
    { label: '(UTC+10:00) Hobart', value: 'Australia/Hobart' },
    { label: '(UTC+10:00) Vladivostok', value: 'Asia/Vladivostok' },
    { label: '(UTC+11:00) Magadan, Solomon Is., New Caledonia', value: 'Asia/Magadan' },
    { label: '(UTC+12:00) Auckland, Wellington', value: 'Pacific/Auckland' },
    { label: '(UTC+12:00) Fiji', value: 'Pacific/Fiji' },
    { label: '(UTC+12:00) Petropavlovsk-Kamchatsky', value: 'Asia/Kamchatka' },
    { label: '(UTC+13:00) Nuku\'alofa', value: 'Pacific/Tongatapu' },
    { label: '(UTC+14:00) Kiritimati Island', value: 'Pacific/Kiritimati' }
  ];
  selectedTimezone = signal(this.detectUserTimezone());

  // Populate brand list from user memberships (reactive)
  private brandsEffect = effect(() => {
    const user = this.membershipService.currentUser();
    const config = this.appConfigService.config$();
    if (!user || !config) return;

    const memberships = user.memberships ?? [];
    const brandList = memberships
      .filter(m => m.isActive)
      .map(m => ({
        id: m.brand.id,
        name: m.brand.name,
        url: m.brand.url,
        privilegeLevel: m.privilegeLevel
      }));

    this.brands.set(brandList);
    this.currentBrand.set(brandList.find(b => b.id === config.brandId) ?? brandList[0] ?? null);

    // Memberships, a divider, then the create action. The action item carries its icon and
    // i18n KEY on data — the menu template branches on labelKey and pipes it, so the label
    // stays reactive on an interface-language switch (a translate.instant here would freeze).
    this.brandMenuItems = [
      ...brandList.map(brand => ({
        label: brand.name,
        command: () => this.onBrandChange(brand),
        icon: 'none',
        data: brand
      })),
      { separator: true },
      {
        id: 'create-brand',
        command: () => this.openCreateBrand(),
        data: { actionIcon: this.faPlus, labelKey: 'topbar.createBrand' }
      }
    ];
  });

  ngOnInit() {
    // Sync with locale service (sets initial values before preferences are loaded)
    this.selectedUILanguage.set(this.localeService.getActiveUILanguage());
    this.selectedContentLanguage.set(this.localeService.getActiveContentLanguage());
    // The bookmarks boot read is the BookmarkService's own, keyed on the session signal — a
    // one-shot check here never ran on the sign-in callback landing, where this chrome
    // initialises before the session resolves.
  }

  /**
   * Detect user's timezone using JavaScript Intl API
   * This is called once when component initializes to set default
   */
  private detectUserTimezone(): string {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Check if detected timezone is in our list
      const timezoneExists = this.timezones.some(tz => tz.value === detectedTimezone);
      if (timezoneExists) {
        return detectedTimezone;
      }
      // If not in list, try to find a close match (same city or region)
      const city = detectedTimezone.split('/').pop();
      const closeMatch = this.timezones.find(tz => 
        tz.value.toLowerCase().includes(city?.toLowerCase() || '')
      );
      if (closeMatch) {
        return closeMatch.value;
      }
    } catch (e) {
      console.error('Failed to detect timezone:', e);
    }
    // Default to UTC if detection fails
    return 'UTC';
  }

  // Brand methods
  onBrandChange(brand: any) {
    if (brand.id === this.currentBrand()?.id) return;
    // A full-page exit to the other brand's domain — ask about unsaved work first (the in-app
    // leave prompt speaks for the work; the browser's own prompt then stands down once).
    void this.leavePrompt.confirmLeaveIfDirty(() => {
      // Navigate to the other brand's origin — the wire's `url` is the ONE home of a brand's
      // address (non-null, selected on every brand summary); never rebuilt from a domain here,
      // which would hard-code the production apex and jump a dev member to production.
      if (brand.url) {
        window.location.href = brand.url;
      }
    });
  }

  // Search - opens the command palette
  openSearch(): void {
    this.commandPaletteService.open();
  }

  // Theme methods
  setTheme(theme: 'light' | 'dark' | 'auto') {
    this.themeService.setTheme(theme);
    this.preferencesService.updatePreference('theme', theme);
  }

  // Timezone methods
  onTimezoneChange() {
    this.preferencesService.updatePreference('timeZone', this.selectedTimezone());
    // TODO: You might want to emit an event or reload date/time displays
  }

  // User methods
  /**
   * A public brand's visitor browsed here inside the app: the shell's redirect note is written at boot alone, so
   * without this note the sign-in or sign-up callback would land them on the portal — or on their boot deep link —
   * instead of the page they were reading. ONE note for both doors, so the two can never disagree. Outside public
   * mode the boot write stays the one home: a member bounced from a deep link keeps that link as their return.
   */
  private noteVisitorReturn(): void {
    if (this.membershipService.publicMode()) {
      sessionStorage.setItem('authRedirectUrl', this.router.url);
    }
  }

  signIn() {
    const returnUrl = window.location.pathname + window.location.search;
    this.noteVisitorReturn();
    this.authService.login(returnUrl, this.signupAllowed());
  }

  signUp() {
    const returnUrl = window.location.pathname + window.location.search;
    this.noteVisitorReturn();
    // Start the sign-up round trip and note "when THIS trip returns, join the person on an
    // Open-policy brand": the note names the hop the sign-in call minted (its OIDC state), so
    // only that hop's return can consume it — never the next person to sign in on this tab.
    // The note is written in the same tick the redirect is started in; the navigation cannot
    // unload the page before the write lands.
    const hop = this.authService.login(returnUrl, this.signupAllowed(), true);
    this.membershipService.markSignupAutoJoin(hop);
  }

  // Membership action methods
  joinBrand() {
    // The admission itself re-boots the document: once the join's re-read lands the member's
    // active standing, the membership service's one state door reloads the page (the ruled road
    // for a standing turning active in place — the reload re-seats every cache and guard answer
    // at once; the tier read, the realtime streams and the bell follow the admission itself) —
    // this door speaks a refusal, or the full-brand sentence when the join became a pending
    // request (the brand had no room) or was refused because that request is already in.
    this.membershipService.joinBrand().subscribe({
      next: outcome => { if (outcome.awaitingApproval) this.sayBrandFull(); },
      error: (error: unknown) => {
        if (primaryCodeOf(error) === 'ALREADY_PENDING') { this.sayBrandFull(); return; }
        // A refused join must speak — a silent arm leaves the member with a button that did nothing.
        console.error('Failed to join brand:', error);
        raiseFailureToast(this.messageService, this.translateService, error, 'requestAccess.errors.joinFailed');
      }
    });
  }

  /** The join became a pending request: the brand is full, the admins have been told. */
  private sayBrandFull(): void {
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('requestAccess.brandFull', { brand: this.brandDisplayName() }),
      life: 6000,
    });
  }

  requestAccess() {
    this.router.navigate(['/request-access']);
  }

  /**
   * Withdraw the member's pending join request — the same confirm-gated journey the Account's
   * My Brands offers: ask, await the server (a membership-corridor act renders nothing before
   * the response), then toast the outcome. The service's own state update clears the pending
   * pill on success.
   */
  cancelJoinRequest() {
    const requestId = this.membershipState().pendingRequestId;
    if (!requestId) return;
    const brandName = this.brandDisplayName();
    raiseConfirm(this.confirmationService, {
      header: this.translateService.instant('topbar.withdrawRequest.title', { brandName }),
      message: this.translateService.instant('topbar.withdrawRequest.body'),
      icon: 'pi pi-question',
      acceptLabel: this.translateService.instant('topbar.withdrawRequest.confirm'),
      rejectLabel: this.translateService.instant('topbar.withdrawRequest.keep'),
      accept: () => {
        this.withdrawing.set(true);
        this.membershipService.cancelJoinRequest(requestId).subscribe({
          next: () => {
            this.withdrawing.set(false);
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('topbar.withdrawRequest.done'),
            });
          },
          error: (error: unknown) => {
            console.error('Failed to withdraw join request:', error);
            this.withdrawing.set(false);
            raiseFailureToast(this.messageService, this.translateService, error, 'topbar.withdrawRequest.failed');
            // A refusal that says the request is already gone re-reads the truth; the pending
            // pill re-derives from the reloaded state instead of asserting a request the server left.
            this.membershipService.reconcileAfterRefusedWithdraw(error);
          }
        });
      }
    });
  }

  // ============================================================================
  // Notifications
  // ============================================================================

  async openNotificationsPanel(): Promise<void> {
    // Lazy-load notifications when the panel actually opens (a toggle-off, or a refused
    // eviction of a dirty panel, resolves false).
    if (await this.notificationsPanel()?.toggle()) {
      this.notificationService.fetchNotifications(true);
    }
  }

  /** A dirty feedback draft's dismiss: the content component owns the prompt and the clear. */
  protected onFeedbackDismissRequested(): void {
    this.feedbackContent()?.promptDiscard(() => this.feedbackPanel()?.close());
  }

  markNotificationRead(id: string) {
    this.notificationService.markRead(id);
  }

  /**
   * Bell-row click: mark read, then deep-link by kind through the ONE routing home
   * (topbar-routing.ts — shared with the email-redirect page so landings never fork),
   * then the stored-destination lane (shares, approved admissions) and the cloud-import
   * destination resolution, which stay click-path-only by design.
   */
  async onNotificationClick(notification: RenderedNotification): Promise<void> {
    this.markNotificationRead(notification.id);

    // Static + subject-keyed kinds (the shared pure resolver; metadata-destination kinds
    // return null there and fall through to their own lane below).
    const descriptor = resolveNotificationRoute(notification);
    if (descriptor) {
      await this.navigateNotificationDescriptor(descriptor);
      return;
    }

    // Destination-carrying bells (shares, approved admissions) navigate the STORED destination —
    // this branch must run BEFORE any subject-keyed routing, or a shared workflow
    // subject would route to the bare pillar page and drop the destination's asOf/anchor context
    // (the shared resolver refuses these kinds for exactly that reason).
    const metadataRoute = resolveMetadataDestinationRoute(notification.eventType, notification.metadata);
    if (metadataRoute) {
      this.notificationsPanel()?.close();
      // The bell IS the member lane's in-app arrival vehicle, with FULL honest-arrival
      // parity with the join-page and stored-destination legs: a locked destination (the
      // member's role lacks the module) diverts to the arrival interstitial that says so —
      // never the route guard's generic toast-and-bounce.
      const reason = await this.shareArrival.lockedArrivalReasonFor(metadataRoute);
      if (reason) {
        // Same personal-now exit as the branches around this one.
        void this.temporal.exitForNavigation('/share-arrival?reason=' + reason, { sameUrlReload: true });
        return;
      }
      // Open destination: record the arrival with the correlation key its metadata carries
      // (share.sent{shareId} → share.arrived{shareId}; the invite lane correlates by
      // invitationId), and mark the arrival context so the destination's page states speak
      // the share's language. A locked destination records neither — nobody arrived. The
      // inviter's in-Trash joined bell correlates nothing: its click is the SHARER going to put
      // their own trashed subject back, not an arrival — no record, no arrival context.
      const correlation = resolveArrivalCorrelation(notification.eventType, notification.metadata);
      if (correlation) {
        this.analytics.shareArrived(correlation);
        this.shareArrival.markArrival(metadataRoute);
      }
      // Same personal-now exit as the branches around this one.
      void this.temporal.exitForNavigation(metadataRoute, { sameUrlReload: true });
      return;
    }

    if (!notification.eventType.startsWith('cloud_import.')) return;

    let metadata: Record<string, unknown> = {};
    try {
      metadata = notification.metadata ? JSON.parse(notification.metadata) : {};
    } catch {
      return; // unparseable metadata — stay put rather than navigate wrong
    }
    const destinationId = typeof metadata['destinationRecordId'] === 'string'
      ? metadata['destinationRecordId'] as string
      : null;
    if (!destinationId) return;

    const url = await this.resolveCloudImportUrl(destinationId, metadata);
    if (!url) return;
    this.notificationsPanel()?.close();
    // Same personal-now exit as the static-route branch above.
    void this.temporal.exitForNavigation(url, { sameUrlReload: true });
  }

  /**
   * A resolved route descriptor from the shared routing home: direct routes navigate as-is;
   * item-subject descriptors fetch the item (URLs need type + slug, which the bell row does
   * not carry) and, for comment kinds, open the host's comments surface — the file preview
   * via the one-shot requested-tab signal set BEFORE navigating, Manual chapters via the
   * comments anchor the landing URL carries. A gone or unreadable item — trashed, purged, or no
   * longer readable here — SAYS so with the journal door's one "isn't available" voice and STAYS
   * (ruled 2026-09-14: the bell had landed the member on the brand home with no word, while the
   * same moment spoke on the journal and wore a face on the e-mail door); the panel stays as it is.
   */
  private async navigateNotificationDescriptor(descriptor: NotificationRouteDescriptor): Promise<void> {
    if (descriptor.route) {
      this.notificationsPanel()?.close();
      // Notification deep-links are personal-now intents — exit "Time travel" first.
      void this.temporal.exitForNavigation(descriptor.route, { sameUrlReload: true });
      return;
    }
    if (!descriptor.itemId) return;

    let url: string;
    try {
      const item = await this.itemService.getItem(descriptor.itemId);
      if (!item) {
        this.activitySubjectOpen.raiseUnavailableToast();
        return;
      }
      if (descriptor.comments) {
        const landing = commentLandingFor(item, this.localeService.slugLocale(), descriptor.commentUrlId);
        if (landing.openCommentsTab) this.filePreviewService.requestedPanelTab.set('comments');
        url = landing.url;
      } else {
        url = buildItemUrl(item, this.localeService.slugLocale());
      }
    } catch (error) {
      // A refused read speaks its own coded sentence through the same home; the member stays.
      this.activitySubjectOpen.raiseUnavailableToast(error);
      return;
    }

    this.notificationsPanel()?.close();
    const left = await this.temporal.exitForNavigation(url, { sameUrlReload: true });
    if (!left) {
      // The navigation was refused (the member kept editing at the leave prompt) — disarm the
      // one-shot comments-tab request armed above, or the NEXT preview open would consume it
      // and jump to the comments tab uninvited.
      this.filePreviewService.requestedPanelTab.set(null);
    }
  }

  /**
   * Destination URL: fetch the container for its slug/urlId; if it's gone (deleted since
   * the import), fall back to the module root derived from the metadata's NUMERIC
   * destinationItemType (the wire value must go through Number(); a string
   * would fall through buildItemUrl's strict-=== switch to /item/…).
   */
  private async resolveCloudImportUrl(destinationId: string, metadata: Record<string, unknown>): Promise<string | null> {
    try {
      const item = await this.itemService.getItem(destinationId);
      if (item) return buildItemUrl(item, this.localeService.slugLocale());
    } catch {
      // fall through to the type-based module root
    }
    const rawType = metadata['destinationItemType'];
    if (rawType == null) return null;
    const type = Number(rawType);
    if (!Number.isFinite(type)) return null;
    // Module roots need no slug — reuse the parts builder with the root types.
    if (type === ItemType.Gallery || type === ItemType.Media) {
      return buildItemUrlFromParts(ItemType.Media, '', '', this.localeService.slugLocale());
    }
    return buildItemUrlFromParts(ItemType.Drive, '', '', this.localeService.slugLocale());
  }

  markAllNotificationsRead() {
    this.notificationService.markAllRead();
  }

  dismissNotification(event: Event, id: string) {
    event.stopPropagation();
    this.notificationService.dismiss(id);
  }

  dismissAllNotifications() {
    this.notificationService.dismissAll();
  }

  // Time travel — lazy-load the density strip on open (notifications precedent)
  async openTimeTravelPanel(): Promise<void> {
    if (await this.timeTravelPanel()?.toggle()) {
      void this.timeTravelContent()?.load();
    }
  }

  // Bookmarks — the list pre-fetch is harmless whichever way the toggle resolves; a list whose last
  // read failed is re-fetched too, so a reopen never re-shows a stale failure face without trying.
  openBookmarksPanel(): void {
    void this.bookmarksPanel()?.toggle();
    if (this.bookmarks().length === 0 || this.bookmarksFailed()) {
      void this.bookmarkService.loadBookmarks();
    }
  }

  /** The failure face's Try again — the same list read, from the top. */
  retryBookmarks(): void {
    void this.bookmarkService.loadBookmarks();
  }

  navigateToBookmark(bookmark: BookmarkItem): void {
    this.bookmarksPanel()?.close();
    this.bookmarkService.navigateToBookmark(bookmark);
  }

  /** A bookmarked item's name through the one editorial read label (the title in the content lens, else the name, else the two-state word). */
  protected bookmarkTitle(bookmark: BookmarkItem): string {
    return this.localeService.editorialLabel(bookmark.item);
  }

  /** Space opens the bookmark like Enter — prevented, so a focused row never scrolls the panel. */
  onBookmarkSpace(event: Event, bookmark: BookmarkItem): void {
    event.preventDefault();
    this.navigateToBookmark(bookmark);
  }

  removeBookmark(event: Event, itemId: string): void {
    event.stopPropagation();
    this.bookmarkService.removeBookmark(itemId);
  }

  signOut() {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/']);
      },
      error: (error) => {
        console.error('Logout failed:', error);
        // Still redirect even if logout had issues
        this.router.navigate(['/']);
      }
    });
  }

  navigateToAccount() {
    // Cross-brand switcher semantics: the topbar's own source (the active switcher brand)
    // is deliberate — other consumers of the shared URL builder supply their own.
    // Full-page exit → unsaved-work pre-flight first.
    const brand = this.currentBrand();
    void this.leavePrompt.confirmLeaveIfDirty(() => {
      window.location.href = buildAccountAppUrl({ brandId: brand?.id || '', brandName: brand?.name || '' });
    });
  }

  openNotificationSettings() {
    // Same brand source as navigateToAccount; the Account app owns the preference page.
    const brand = this.currentBrand();
    void this.leavePrompt.confirmLeaveIfDirty(() => {
      window.location.href = buildAccountAppUrl({ path: '/notifications', brandId: brand?.id || '', brandName: brand?.name || '' });
    });
  }

  navigateToActivity() {
    this.profilePanel()?.close();
    this.router.navigate(['/activity']);
  }

  navigateToSharesSent() {
    this.profilePanel()?.close();
    this.router.navigate(['/shares']);
  }

  /** The profile item's door to the shortcut reference: close the menu, then open the panel. */
  openKeyboardShortcuts() {
    this.profilePanel()?.close();
    this.shortcutRegistry.helpOpen.set(true);
  }

  /** Space activates the notification like Enter — prevented, so a focused row never scrolls the panel. */
  onNotificationSpace(event: Event, notification: RenderedNotification): void {
    event.preventDefault();
    void this.onNotificationClick(notification);
  }

  /**
   * The search button's keyboard chip — derived from the reference's own Open-search row, so the
   * two surfaces can never teach different keys. EMPTY in Time travel: the palette refuses both
   * keys there, and a disabled button must not keep advertising dead ones (the button and its
   * today-only tooltip stay — the affordance remains discoverable).
   */
  searchShortcutKeys(): readonly string[] {
    return this.temporal.active() ? [] : OPEN_SEARCH_SHORTCUT.keys;
  }

  // Global keyboard handling moved to CommandPaletteComponent
  // It handles: Cmd+K, "/", Escape for search
  
  showBrandMenu(event: Event) {
    // Clickable from the FIRST brand: even a one-brand member gets the menu (their brand +
    // the create road) — a selector that only worked at two brands hid brand creation from
    // exactly the people most likely to want a second brand.
    if (this.brands().length > 0) {
      this.brandMenu()?.toggle(event);
    }
  }

  openCreateBrand() {
    // Brand creation lives on the marketing site — the app only links there.
    // Full-page exit → unsaved-work pre-flight first.
    void this.leavePrompt.confirmLeaveIfDirty(() => {
      window.location.href = `${environment.marketingBaseUrl}/start`;
    });
  }
  
  // Language change handlers
  onUILanguageChange(language?: string) {
    // Use the bound model value since we're using ngModel. Graceful setter keeps us robust to a
    // stale selection; persist the language actually applied (and resync the picker if it differs).
    const requested = language || this.selectedUILanguage();
    // The content language BEFORE this switch, sampled ahead of the persist: the preferences service applies the
    // resolved content language itself, synchronously, inside `updatePreference` (before its first await), so a
    // sample taken after the persist already read the moved language and the once-per-switch toast below could
    // never fire — the app's own ordering, invisible to a spec whose persist double did nothing.
    const before = this.localeService.getActiveContentLanguage();
    const lang = this.localeService.setActiveUILanguageOrDefault(requested);
    if (lang !== requested) this.selectedUILanguage.set(lang);
    this.preferencesService.updatePreference('displayLanguage', lang);

    // If the user has NO explicit per-brand content pick, content follows the UI
    // language ("content in the language I read"); explicit picks are left untouched.
    const brandId = this.appConfigService.getValue('brandId') as string | undefined;
    const prefs = this.preferencesService.preferences();
    // "Has a pick" is the ONE predicate the resolver answers by (a stored pick the brand offers today): a pick for a
    // language the brand no longer offers is no pick, so such a reader's content moves with the interface language
    // like anyone's, and is told so below — never a silent move because the map happened to hold an entry.
    const picks = Object.fromEntries((prefs.contentLanguageByBrand ?? []).map(e => [e.brandId, e.locale]));
    const hasExplicitPick = this.localeService.hasContentLanguagePick(picks, brandId);
    if (!hasExplicitPick) {
      // The persist above has already applied the resolved content language — synchronously, before its first
      // await — from the same per-brand picks, so `after` is read here, straight after the call and never after
      // awaiting it: a save still in flight (or one that fails later and rolls back) must not decide what the
      // toast says.
      const after = this.localeService.getActiveContentLanguage();
      this.selectedContentLanguage.set(after);
      // The content select may move while the panel is closed: say once, per switch that repositions
      // it, where content went (the boot and a brand switch resolve the same way but the member did
      // not act there — those stay silent; the panel's sentence is the standing disclosure). The raises
      // land in switch order (the queue below).
      if (after !== before) this.contentFollowedRaises = this.contentFollowedRaises.then(() => this.raiseContentFollowedToast(lang, after));
    }
  }

  /**
   * The content-followed toasts land in SWITCH order: each raise waits for the previous one's catalog read. A
   * first switch into a catalog not yet loaded awaits its round trip while a second, back into a loaded one,
   * resolves in a microtask — unchained, the later switch's sentence landed first and the superseded one LAST,
   * naming a content language no longer active in an interface language the member had left. Both toasts stay
   * (once per switch is the ruled frequency; a superseded raise is never dropped). The raise never rejects (a
   * failed catalog is dropped with a console warning), so the chain never breaks.
   */
  private contentFollowedRaises: Promise<void> = Promise.resolve();

  /**
   * The once-per-switch toast, composed IN the interface language the member just switched TO: the
   * catalog switch runs in an app-level effect after this handler, so a synchronous `instant` here read
   * the catalog just LEFT while the language name already came in the new one — a Czech sentence naming
   * "English", every time. Both strings resolve through the target language's own catalog (read once per
   * session) and the namer displayed in it.
   */
  private async raiseContentFollowedToast(uiLanguage: string, contentLanguage: string): Promise<void> {
    // Named AMONG the brand's offer (ruled 2026-09-12: a sibling variant in the offer qualifies the name by region)
    // PLUS the interface language the member just switched to (ratified 2026-09-14): a reader repositioned onto a
    // sibling of their own language — en-GB for an en-US reader, the resolver's silent landing — reads WHICH one
    // ("English (United Kingdom)"), exactly where that landing's accepted cost is paid; an unrelated landing, or an
    // exact match, gains nothing from the extra member of the set (the namer skips the code's own entry).
    const language = this.localeService.languageName(contentLanguage, {
      displayLocale: uiLanguage,
      among: [...this.localeService.getSupportedContentLanguages(), uiLanguage],
    });
    // The target catalog's read REJECTS when it fails to load (the caller decides): the switch has already
    // landed and the toast is cosmetic, so a failed catalog drops it and says nothing — never an
    // unhandled rejection escaping to the global error handler and its generic failure toast.
    try {
      const [summary, detail] = await Promise.all([
        this.localeService.translateIn(uiLanguage, 'topbar.contentLanguage'),
        this.localeService.translateIn(uiLanguage, 'topbar.contentLanguageFollowed', { language }),
      ]);
      this.messageService.add({ severity: 'info', summary, detail });
    } catch (error) {
      console.warn(`The ${uiLanguage} catalog did not load for the content-language toast; the switch landed, the toast is dropped.`, error);
    }
  }
  
  onContentLanguageChange(language?: string) {
    // Use the bound model value since we're using ngModel
    const lang = language || this.selectedContentLanguage();
    this.localeService.setActiveContentLanguage(lang);
    // Per-brand memory: the choice is saved for THIS brand through the content language's own save (the request
    // is addressed to this brand; the id keys this browser's copy). A monitoring root — a platform root reading a
    // brand it is no member of — keeps no per-brand choice: the server refuses the save by name, so nothing is sent
    // for it and the lens switches for the visit alone (the Manual view's switch, the sibling per-brand save, skips
    // the same way). Sending it would only earn the root a warning toast at every switch.
    const brandId = this.appConfigService.getValue('brandId') as string | undefined;
    if (brandId && !this.access.isReadOnlyRoot()) {
      this.preferencesService.setContentLanguageForBrand(brandId, lang);
    }
  }
}
